import { useState } from 'react';
import styled, { css } from 'styled-components';

import { Choice } from 'shared/components/Choice';
import { ChoiceLabel } from 'search/components/searchGrid/SearchFilterSizeMobileChoiceLabel';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { IConstructorFacetMultiple } from 'shared/types/ConstructorFacet';
import { variableFontRegular } from 'styles/theme/text';
import { sortGender, sortSizesByDisplayName } from 'search/utils/sizeFilterUtils';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { getConstructorCategoriesStrings } from 'search/enums/translations';
import { startCase } from 'shared/utils/startCase';
import { t } from 'localization/utils/t';

const TP = 'search.components.searchGrid.SearchFilterSizeMobileProductType';

interface ISizeFilterProductType {
  productType: string;
  sizeOptions: Array<IConstructorFacetMultiple>;
  selectedFilterValues: Array<string>;
  setSelectedFilterValues: (options: Array<string>) => void;
  showSizeConversions: boolean;
}

export const SizeFilterProductType = ({
  productType,
  sizeOptions,
  selectedFilterValues,
  setSelectedFilterValues,
  showSizeConversions,
}: ISizeFilterProductType) => {
  const { user } = useStoreSelector((state) => state.auth);
  const userSizePreferenceUnit = user?.convertedSizeUnit || 'us';
  const userSizeGender = user?.sizeGender;
  const genders = Object.keys(sizeOptions).sort(sortGender);
  const userGenderPreferenceIndex = genders.indexOf(userSizeGender);
  if (userGenderPreferenceIndex > -1) {
    genders.splice(userGenderPreferenceIndex, 1);
    genders.unshift(userSizeGender);
  }
  const [selectedGender, setSelectedGender] = useState(genders[0]);

  const options = sizeOptions?.[selectedGender]
    ?.sort(sortSizesByDisplayName)
    .filter((option) => option.display_name);

  const handleGenderClick = (val) => {
    setSelectedGender(val);
  };

  const handleOnChange = (optionValue) => {
    if (selectedFilterValues.includes(optionValue)) {
      const updatedValues = selectedFilterValues.filter((value) => {
        return value !== optionValue;
      });
      setSelectedFilterValues(updatedValues);
    } else {
      setSelectedFilterValues([...selectedFilterValues, optionValue]);
    }
  };

  if (!options || options.length === 0) {
    return null;
  }

  return (
    <SizeFilterProductType.Wrapper>
      <SizeFilterProductType.HeaderWrapper>
        <SizeFilterProductType.Title data-qa="size_filter_product_type">
          {getConstructorCategoriesStrings(productType === 'shoes' ? 'sneakers' : productType)}
        </SizeFilterProductType.Title>
        <SizeFilterProductType.GenderWrapper>
          {genders.map((gender, idx) => (
            <SizeFilterProductType.Gender
              $isSelected={selectedGender === gender}
              data-qa="size_filter_gender"
              key={gender}
              onClick={() => handleGenderClick(gender)}
            >
              {idx === 0
                ? t(`${TP}.unitGender`, '{unit} {gender}', {
                    unit: userSizePreferenceUnit?.toUpperCase(),
                    gender: startCase(getConstructorCategoriesStrings(gender), {
                      capitalizeInOtherLanguages: false,
                    }),
                  })
                : getConstructorCategoriesStrings(gender)}
            </SizeFilterProductType.Gender>
          ))}
        </SizeFilterProductType.GenderWrapper>
      </SizeFilterProductType.HeaderWrapper>
      <SizeFilterProductType.ChoiceWrapper data-body-scroll-lock-ignore>
        {options.map((option) => {
          return (
            <SizeFilterProductType.Choice
              checked={
                Array.isArray(selectedFilterValues) && selectedFilterValues.includes(option.value)
              }
              data-qa="size_filter_size_option"
              key={option.value}
              label={
                <ChoiceLabel
                  option={option}
                  productType={productType}
                  selectedGender={selectedGender}
                  showSizeConversions={showSizeConversions}
                  userSizePreferenceUnit={userSizePreferenceUnit}
                />
              }
              onChange={() => handleOnChange(option.value)}
              type="checkbox"
              $showSizeConversions={showSizeConversions}
              $isOneSize={option.value.includes('one_size')}
              value={option.value}
            />
          );
        })}
      </SizeFilterProductType.ChoiceWrapper>
    </SizeFilterProductType.Wrapper>
  );
};

SizeFilterProductType.Wrapper = styled.div`
  margin-bottom: 32px;
`;

SizeFilterProductType.HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

SizeFilterProductType.Title = styled.h3`
  text-transform: capitalize;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  font-weight: 400;
  margin: 0;
`;

SizeFilterProductType.GenderWrapper = styled.div`
  display: flex;
  ${variableFontRegular({ size: '11.5px', height: '12px' })};
`;

SizeFilterProductType.Gender = styled.div<{ $isSelected: boolean }>`
  text-transform: capitalize;
  margin-left: 16px;
  text-decoration: ${({ $isSelected }) => $isSelected && 'underline'};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

SizeFilterProductType.GenderUnit = styled.span`
  text-transform: uppercase;
`;

SizeFilterProductType.ChoiceWrapper = styled.div`
  margin: 16px -16px 0;
  display: flex;
  overflow-x: scroll;
  ${hideScrollbars};
`;

SizeFilterProductType.Choice = styled(Choice)<{
  $showSizeConversions: boolean;
  $isOneSize: boolean;
  checked: boolean;
}>`
  border: ${({ theme }) => theme.navigation.border};
  margin: 0 8px;
  width: 38px;
  min-width: 38px;
  height: 38px;

  ${({ $isOneSize }) =>
    $isOneSize &&
    css`
      width: 60px;
      min-width: 60px;
    `}

  ${({ $showSizeConversions }) =>
    $showSizeConversions &&
    css`
      padding: 8px 16px 16px 8px;
      min-width: auto;
      width: auto;
      max-height: 90px;
      height: 90px;
    `}

    ${({ checked }) =>
    checked &&
    css`
      box-shadow: 0 0 0 1px black inset;
    `}

  &:first-child {
    margin-left: 16px;
  }
  &:last-child {
    margin-right: 16px;
  }
`;
